// src/App.js
import React, { useState, useEffect } from "react";
import "./App.css";
import { db } from "./firebase";
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import songs from "./songs";
import {
    BrowserRouter,
    Routes,
    Route,
} from "react-router-dom";


const medals = [
    { points: 3, name: "gold", image: "/images/3p.png" },
    { points: 2, name: "silver", image: "/images/2p.png" },
    { points: 1, name: "bronze", image: "/images/1p.png" },
];

function getUserId() {
    let uid = localStorage.getItem("user_uuid");
    if (!uid) {
        uid = uuidv4();
        localStorage.setItem("user_uuid", uid);
    }
    return uid;
}

function App() {
    const [votes, setVotes] = useState({});
    const [hasVoted, setHasVoted] = useState(false);
    const [votingVersion, setVotingVersion] = useState(null);

    const [usedPoints, setUsedPoints] = useState([]);
    const [selectedMedal, setSelectedMedal] = useState(null);
    const [isVotingOpen, setIsVotingOpen] = useState(null); // null = cargando

    useEffect(() => {
        async function checkVotingStatus() {
            try {
                const configRef = doc(db, "config", "voting");
                const snap = await getDoc(configRef);

                if (snap.exists()) {
                    const data = snap.data();
                    setIsVotingOpen(data.isVotingEnabled);

                    const currentVersion = data.votingVersion ?? 1;
                    setVotingVersion(currentVersion);

                    // Obtener versión almacenada en localStorage
                    const storedVersion = parseInt(localStorage.getItem("has_voted_version"), 10);
                    const alreadyVoted = !isNaN(storedVersion) && storedVersion === currentVersion;

                    setHasVoted(alreadyVoted);

                } else {
                    console.warn("El documento /config/voting no existe.");
                    setIsVotingOpen(false);
                }
            } catch (err) {
                console.error("Error leyendo la config de votación:", err);
                setIsVotingOpen(false);
            }
        }

        checkVotingStatus();
    }, []);

    function handleMedalClick(points) {
        if (usedPoints.includes(points)) return;
        setSelectedMedal(points);
    }

    function handleSongClick(songId) {
        if (selectedMedal == null) return;

        // ✅ Verifica si ya se ha votado esta canción
        if (votes[songId]) {
            alert("Ya has asignado una medalla a esta canción.");
            return;
        }

        // ✅ Verifica si ese valor de medalla ya fue usado
        if (Object.values(votes).includes(selectedMedal)) return;

        setVotes((prev) => ({ ...prev, [songId]: selectedMedal }));
        setUsedPoints((prev) => [...prev, selectedMedal]);
        setSelectedMedal(null);
    }

    function resetVotes() {
        setVotes({});
        setUsedPoints([]);
        setSelectedMedal(null);
    }

    async function submitVote() {

        if (hasVoted) return;

        const userId = getUserId();

        // Verifica que los puntos 1, 2 y 3 están asignados
        const assignedPoints = Object.values(votes);
        const hasAll = [1, 2, 3].every((p) => assignedPoints.includes(p));

        if (!hasAll) {
            alert("❗ Debes asignar los puntos antes de confirmar tu voto.");
            return;
        }

        const voteList = Object.entries(votes).map(([songId, points]) => ({
            songId,
            points,
        }));

        await addDoc(collection(db, "votes"), {
            userId,
            votes: voteList,
            timestamp: serverTimestamp(),
        });


        localStorage.setItem("has_voted_version", votingVersion);
        setHasVoted(true);
    }

    if (hasVoted) {
        return (
            <div className="thank-you-screen">
                <img
                    src="/images/LOGOFINAL.png"
                    alt="Logo final"
                    className="thank-you-logo"
                />
                <h2>¡Gracias por votar! 🥳</h2>
            </div>
        );
    }

    if (isVotingOpen === null) {
        // Todavía se está cargando el estado desde Firestore
        return (
            <div style={{ textAlign: "center", marginTop: "5rem" }}>
                <h2>Cargando...</h2>
            </div>
        );
    }

    if (!isVotingOpen) {
        // La votación está desactivada: mostrar pantalla de espera
        return (
            <div
                className="thank-you-screen"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    textAlign: "center",
                    padding: "2rem"
                }}
            >
                <img
                    src="/images/LOGOFINAL.png"
                    alt="Logo final"
                    style={{ width: "300px", maxWidth: "80%", marginBottom: "2rem" }}
                />
                <h2 style={{ fontSize: "1.8rem" }}>
                    Prepárate para votar al final del espectáculo 🎤
                    <br />
                    <br />
                    <h5 >- Cuando te avisemos, refresca la página para votar. -</h5>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: "1rem",
                            padding: "0.5rem 1.5rem",
                            fontSize: "1.2rem",
                            cursor: "pointer",
                            backgroundColor: "#000000",   // negro
                            color: "#FFFFFF",             
                            border: "3px solid #ff0000",
                            borderRadius: "6px",
                            fontWeight: "bold",
                            boxShadow: "0 0 10px #ff0000",
                            transition: "background-color 0.3s, color 0.3s",
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = "#FFEA00";
                            e.currentTarget.style.color = "#000000";
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = "#000000";
                            e.currentTarget.style.color = "#FFEA00";
                        }}
                    >
                        Recargar página
                    </button>
                </h2>
            </div>
        );
    }

    return (
        <div className="App">
            <header className="app-header">
                <img src="/images/LOGOFINAL.png" alt="Logo" className="logo-header" />
                <h1>Vota tus canciones favoritas</h1>
            </header>

            <div className="medals-container">
                <div className="medals">
                    {medals.map((medal, index) => {
                        const isUsed = usedPoints.includes(medal.points);
                        const isSelected = selectedMedal === medal.points;

                        return (
                            <button
                                key={index}
                                disabled={isUsed}
                                onClick={() => handleMedalClick(medal.points)}
                                className="medal"
                                style={{
                                    backgroundColor: isUsed ? "#eee" : isSelected ? "#ffeaa7" : "white",
                                    border: isSelected ? "2px solid #f1c40f" : "2px dashed #ccc",
                                }}
                            >
                                <img
                                    src={isUsed ? "/images/check.png" : medal.image}
                                    alt={medal.name}
                                    style={{ width: "60px", height: "60px" }}
                                />
                            </button>
                        );
                    })}
                </div>

                {Object.keys(votes).length > 0 && (
                    <button className="reset" onClick={resetVotes}>
                        Resetear votos
                    </button>
                )}
            </div>

            <div className="song-list">
                {songs.map((song) => (
                    <div
                        key={song.id}
                        onClick={() => handleSongClick(song.id)}
                        className="song"
                    >
                        <span>{song.name}</span>
                        <span className="vote-result">
                            {votes[song.id] ? (
                                <img
                                    src={
                                        medals.find((m) => m.points === votes[song.id])?.image
                                    }
                                    alt="medalla"
                                    style={{ width: "40px", height: "40px" }}
                                />
                            ) : (
                                ""
                            )}
                        </span>
                    </div>
                ))}
            </div>

            <button className="submit" onClick={submitVote}>
                Confirmar votación
            </button>
        </div>
    );
}



export default App;
